<script setup lang="ts" generic="Row extends OutboundRow">
import { useI18n } from "vue-i18n";
import type { OutboundRow } from "./cnmcTypes";

/**
 * The form rows of a CNMC transfer or export, one per source receipt, installation and production
 * year. The caller adds the month and amount columns through the `headers` and `cells` slots.
 */
defineProps<{ rows: Row[] }>();

defineSlots<{
  headers(): unknown;
  cells(props: { row: Row }): unknown;
}>();

const { t } = useI18n();

const rowKey = (row: OutboundRow) =>
  `${row.source_receipt}|${row.installation_code}|${row.production_year}`;
</script>

<template>
  <div class="cnmc-rows-table table-responsive border rounded-3">
    <table class="table table-sm mb-0 align-middle">
      <thead>
        <tr>
          <th scope="col">{{ t("cnmc.outbound.review.plant") }}</th>
          <th scope="col">{{ t("cnmc.outbound.review.source_receipt") }}</th>
          <slot name="headers" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="rowKey(row)">
          <td class="text-break">
            <div class="fw-medium">{{ row.device_name }}</div>
            <div class="small text-body-secondary">
              {{ row.installation_code }}
            </div>
          </td>
          <td class="small text-break">{{ row.source_receipt }}</td>
          <slot name="cells" :row />
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
// table-sm's 0.25rem suits a borderless table; inside the rounded border the outer columns need room.
// :deep() so the month and amount cells the caller slots in get the same padding.
.cnmc-rows-table :deep(tr > *) {
  padding: 0.5rem 0.75rem;
}

.cnmc-rows-table :deep(tr > :first-child) {
  padding-inline-start: 1rem;
}

.cnmc-rows-table :deep(tr > :last-child) {
  padding-inline-end: 1rem;
}
</style>
